import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO } from "date-fns";
import { Check, ChevronDown, ChevronUp, X, CalendarCheck, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useBackfillChecklistStore, type BackfillDay } from "@/stores/backfillChecklistStore";

interface BackfillChecklistProps {
  /** Called when user clicks a date to navigate to it */
  onNavigateToDate: (date: string) => void;
  /** Current selected date on Track page (YYYY-MM-DD) */
  currentDate?: string;
}

export function BackfillChecklist({ onNavigateToDate, currentDate }: BackfillChecklistProps) {
  const { t } = useTranslation();
  const { visible, expanded, days, toggleExpanded, close, markDayDone } = useBackfillChecklistStore();

  const completedCount = useMemo(() => days.filter(d => d.done).length, [days]);
  const totalCount = days.length;
  const allDone = completedCount === totalCount && totalCount > 0;

  if (!visible || totalCount === 0) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className={cn(
            "fixed z-[90] shadow-2xl rounded-xl border border-border/60 overflow-hidden",
            "bg-background/85 backdrop-blur-xl",
            "right-3",
            // Position above MobileBottomNav (which is ~64px + safe area)
            "bottom-[calc(5rem+env(safe-area-inset-bottom,0px))]",
          )}
          style={{ maxWidth: "min(320px, calc(100vw - 24px))" }}
        >
          {/* Header - always visible */}
          <button
            onClick={toggleExpanded}
            className={cn(
              "flex items-center justify-between w-full px-3 py-2.5 gap-2",
              "hover:bg-muted/30 transition-colors",
              allDone && "bg-green-500/10"
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              <CalendarCheck className={cn(
                "w-4 h-4 flex-shrink-0",
                allDone ? "text-green-500" : "text-primary"
              )} />
              <span className="text-sm font-medium truncate">
                {allDone
                  ? t("backfill_checklist.all_done", "All caught up!")
                  : t("backfill_checklist.title", "Days to log")}
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className={cn(
                "text-xs font-medium px-1.5 py-0.5 rounded-full",
                allDone
                  ? "bg-green-500/20 text-green-600 dark:text-green-400"
                  : "bg-primary/10 text-primary"
              )}>
                {completedCount}/{totalCount}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); close(); }}
                className="p-0.5 rounded-md hover:bg-muted/50 transition-colors"
                aria-label={t('backfill_checklist.close_checklist')}
              >
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
              {expanded
                ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                : <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />}
            </div>
          </button>

          {/* Expanded content */}
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="px-2 pb-2 max-h-[40vh] overflow-y-auto scrollbar-none">
                  <div className="space-y-1">
                    {days.map((day) => (
                      <DayRow
                        key={day.date}
                        day={day}
                        isCurrentDate={currentDate === day.date}
                        onNavigate={() => onNavigateToDate(day.date)}
                        onMarkDone={() => markDayDone(day.date)}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Progress bar */}
          <div className="h-1 bg-muted/30">
            <motion.div
              className={cn(
                "h-full",
                allDone ? "bg-green-500" : "bg-primary"
              )}
              initial={{ width: 0 }}
              animate={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DayRow({
  day,
  isCurrentDate,
  onNavigate,
  onMarkDone,
}: {
  day: BackfillDay;
  isCurrentDate: boolean;
  onNavigate: () => void;
  onMarkDone: () => void;
}) {
  const { t } = useTranslation();
  const parsed = parseISO(day.date);
  const displayDate = format(parsed, "EEE, MMM d");
  const emojis = day.metricEmojis.slice(0, 3).join("");
  const extraCount = day.metricEmojis.length > 3 ? day.metricEmojis.length - 3 : 0;

  return (
    <motion.div
      layout
      className={cn(
        "flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors group",
        day.done
          ? "opacity-60"
          : isCurrentDate
            ? "bg-primary/10 ring-1 ring-primary/30"
            : "hover:bg-muted/40"
      )}
    >
      {/* Check circle / mark done button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!day.done) onMarkDone();
        }}
        disabled={day.done}
        className={cn(
          "flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
          day.done
            ? "bg-green-500 border-green-500 text-white"
            : "border-muted-foreground/40 hover:border-primary hover:bg-primary/10"
        )}
        title={day.done ? t("backfill_checklist.done", "Done") : t("backfill_checklist.mark_done", "Mark as done")}
      >
        {day.done && <Check className="w-3 h-3" />}
      </button>

      {/* Date and metrics info */}
      <button
        onClick={onNavigate}
        disabled={day.done}
        className={cn(
          "flex-1 text-left min-w-0",
          day.done && "pointer-events-none"
        )}
      >
        <div className="flex items-center gap-1.5">
          <span className={cn(
            "text-xs font-medium",
            day.done ? "line-through text-muted-foreground" : "text-foreground"
          )}>
            {displayDate}
          </span>
          {isCurrentDate && !day.done && (
            <span className="text-[10px] px-1 py-0.5 bg-primary/20 text-primary rounded font-medium">
              {t("backfill_checklist.current", "now")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="text-[11px]">{emojis}</span>
          {extraCount > 0 && (
            <span className="text-[10px] text-muted-foreground">+{extraCount}</span>
          )}
          <span className="text-[10px] text-muted-foreground truncate">
            {day.metricLabels.slice(0, 2).join(", ")}
            {day.metricLabels.length > 2 && ` +${day.metricLabels.length - 2}`}
          </span>
        </div>
      </button>

      {/* Go arrow */}
      {!day.done && (
        <button
          onClick={onNavigate}
          className="flex-shrink-0 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-muted/50 transition-all"
          aria-label={`Go to ${displayDate}`}
        >
          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      )}
    </motion.div>
  );
}
